import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ReturnPqrsdDialog from "./ReturnPqrsdDialog";
import RegisterSignature from "../task/RegisterSignature";

import { pqrsdText } from "./pqrsdLabel";
// Officer-side view of the requerimientos de información on a PQRSD: what was
// asked, what the citizen answered, and the two actions that can close the
// cycle — withdraw it (term resumes) or decree the desistimiento (art. 17).

export const STATUS_CHIP = {
  draft: { label: "Borrador", color: "default" },
  pending_approval: { label: "Pendiente de aprobación", color: "warning" },
  sent: { label: "Enviado al ciudadano", color: "info" },
  replied: { label: "Respondido", color: "success" },
  expired: { label: "Vencido", color: "error" },
  withdrawn: { label: "Retirado", color: "default" },
};

function formatDate(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function daysLeft(dueAt) {
  if (!dueAt) return null;
  const d = dueAt instanceof Date ? dueAt : new Date(dueAt);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

export default function InfoRequestPanel({
  requests = [],
  pqrsd,
  canRequestInfo = false,
  canApprove = false,
  canDecree = false,
  busy = false,
  onApprove,
  onWithdraw,
  onExtend,
  onDecree,
  onResendLink,
  onEdit,
  onSubmitReview,
  onReject,
  onDelete,
  onRetract,
}) {
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [decreeOpen, setDecreeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  // Approving is what finalizes the requerimiento, so the approver — not the
  // drafter — is the one whose name the document is signed with.
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveSigned, setApproveSigned] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [linkNotice, setLinkNotice] = useState(null);

  if (!requests.length) return null;

  // A draft nobody has sent yet is as "active" as one under review: both are
  // work in progress that the panel has to offer actions on.
  const active = requests.find(
    (r) => r.status === "sent" || r.status === "pending_approval" || r.status === "draft"
  ) || null;
  const left = active?.status === "sent" ? daysLeft(active.dueAt) : null;
  const desistimientoDue = !!pqrsd?.desistimientoDue;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Typography variant="h6">Requerimientos de información</Typography>
        <Typography variant="caption" color="text.secondary">
          Artículo 17 de la Ley 1755 de 2015
        </Typography>
      </Stack>

      {active?.status === "sent" && (
        <Alert
          severity={desistimientoDue ? "error" : left != null && left <= 3 ? "warning" : "info"}
          icon={<ScheduleIcon />}
          sx={{ mb: 2 }}
        >
          {desistimientoDue ? (
            <>
              El plazo del ciudadano venció el <strong>{formatDate(active.dueAt)}</strong> sin
              respuesta. Puede decretar el desistimiento tácito mediante acto motivado, o retirar el
              requerimiento y continuar el trámite. El sistema no archiva por su cuenta.
            </>
          ) : (
            <>
              El término de respuesta está <strong>suspendido</strong>. El ciudadano tiene plazo
              hasta el <strong>{formatDate(active.dueAt)}</strong>
              {left != null && left >= 0 ? ` (${left} día${left === 1 ? "" : "s"})` : ""}.
            </>
          )}
        </Alert>
      )}

      {active?.status === "pending_approval" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Este requerimiento está pendiente de aprobación. El término sigue corriendo hasta que se
          apruebe y se envíe al ciudadano.
        </Alert>
      )}

      <Stack spacing={2} divider={<Divider flexItem />}>
        {requests.slice().reverse().map((r) => {
          const chip = STATUS_CHIP[r.status] || { label: r.status, color: "default" };
          const items = [...(r.items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
          return (
            <Box key={r.id}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip size="small" label={chip.label} color={chip.color} />
                {r.sentAt && (
                  <Typography variant="body2" color="text.secondary">
                    Enviado {formatDate(r.sentAt)}
                  </Typography>
                )}
                {r.requerimientoFiledId && (
                  // The requerimiento is a filed document of its own; the
                  // radicado opens it rather than only naming it.
                  <Link
                    href={r.requerimientoFileUrl || undefined}
                    onClick={(e) => {
                      if (r.requerimientoFileUrl) return;
                      e.preventDefault();
                      if (r.requerimientoReferenceId) {
                        window.open(`/filed/view/${encodeURIComponent(r.requerimientoReferenceId)}`, "_blank");
                      }
                    }}
                    target="_blank"
                    rel="noopener"
                    variant="body2"
                    sx={{ fontFamily: "monospace", display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    <DescriptionIcon fontSize="small" /> {r.requerimientoFiledId}
                  </Link>
                )}
                {r.lateIssue && (
                  <Tooltip title="Se emitió después de los 10 días hábiles siguientes a la radicación">
                    <Chip size="small" variant="outlined" color="warning" label="Fuera de los 10 días" />
                  </Tooltip>
                )}
                {r.extensionGrantedAt && (
                  <Chip size="small" variant="outlined" label="Prórroga concedida" />
                )}
                {r.reply?.viaAppeal ? (
                  <Chip size="small" variant="outlined" color="warning" label="Aportado con el recurso de reposición" />
                ) : r.reply?.late && (
                  <Chip size="small" variant="outlined" color="warning" label="Respondido fuera de plazo" />
                )}
              </Stack>

              {r.note && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: "italic" }}>
                  “{r.note}”
                </Typography>
              )}

              <Stack spacing={1.5} sx={{ pl: 1, borderLeft: 3, borderColor: "divider" }}>
                {items.map((it) => (
                  <Box key={it.id} sx={{ pl: 1 }}>
                    <Typography variant="subtitle2">
                      {it.kind === "file" ? it.label : it.header}
                      {it.required === false && (
                        <Typography component="span" variant="caption" color="text.secondary"> (opcional)</Typography>
                      )}
                    </Typography>
                    {it.kind === "file" ? (
                      it.file?.url ? (
                        <Link href={it.file.url} target="_blank" rel="noopener" variant="body2"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          <DescriptionIcon fontSize="small" /> {it.file.name}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Sin cargar</Typography>
                      )
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}
                        color={it.answer ? "text.primary" : "text.secondary"}>
                        {it.answer || "Sin responder"}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>

              {r.status === "replied" && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Respondido {formatDate(r.repliedAt)}
                  </Typography>
                </Stack>
              )}
              {r.withdrawnReason && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Retirado: {r.withdrawnReason}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>

      {linkNotice && (
        <Alert severity={linkNotice.severity} sx={{ mt: 2 }} onClose={() => setLinkNotice(null)}>
          {linkNotice.text}
        </Alert>
      )}

      {active && (
        <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap">
          {(active.status === "draft" || active.status === "pending_approval") && canRequestInfo && (
            <>
              {onEdit && (
                <Button variant="outlined" startIcon={<EditIcon />} disabled={busy} onClick={() => onEdit(active)}>
                  {active.status === "draft" ? "Editar borrador" : "Revisar y editar"}
                </Button>
              )}
              {onSubmitReview && active.status === "draft" && (
                <Button variant="outlined" disabled={busy} onClick={() => onSubmitReview(active.id)}>
                  Enviar a revisión
                </Button>
              )}
              {onRetract && active.status === "pending_approval" && (
                <Button
                  variant="outlined"
                  startIcon={<UndoIcon />}
                  disabled={busy}
                  onClick={() => onRetract(active.id)}
                >
                  Retirar de la cola
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={busy}
                  onClick={() => onDelete(active.id)}
                >
                  Eliminar borrador
                </Button>
              )}
            </>
          )}
          {active.status === "pending_approval" && canApprove && (
            <>
              <Button variant="contained" disabled={busy} onClick={() => setApproveOpen(true)}>
                Aprobar y enviar
              </Button>
              {onReject && (
                <Button variant="outlined" color="warning" disabled={busy} onClick={() => setRejectOpen(true)}>
                  Devolver a borrador
                </Button>
              )}
            </>
          )}
          {active.status === "sent" && canRequestInfo && (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={busy}
                onClick={async () => {
                  try {
                    const { replyLink } = await onResendLink(active.id, { sendEmail: false });
                    // Clipboard access can be denied (insecure origin, permission);
                    // fall back to showing the link so it stays usable.
                    try {
                      await navigator.clipboard.writeText(replyLink);
                      setLinkNotice({ severity: "success", text: "Enlace del ciudadano copiado al portapapeles." });
                    } catch (_) {
                      setLinkNotice({ severity: "info", text: `Enlace del ciudadano: ${replyLink}` });
                    }
                  } catch (e) {
                    setLinkNotice({ severity: "error", text: e.message });
                  }
                }}
              >
                Copiar enlace
              </Button>
              <Button
                variant="outlined"
                startIcon={<ForwardToInboxIcon />}
                disabled={busy}
                onClick={async () => {
                  try {
                    await onResendLink(active.id, { sendEmail: true });
                    setLinkNotice({ severity: "success", text: "Reenviamos el requerimiento al correo del ciudadano." });
                  } catch (e) {
                    setLinkNotice({ severity: "error", text: e.message });
                  }
                }}
              >
                Reenviar correo
              </Button>
              <Button variant="outlined" color="inherit" disabled={busy} onClick={() => setWithdrawOpen(true)}>
                Retirar requerimiento
              </Button>
              {/* Art. 17: the prórroga is asked for before the plazo runs out;
                  once it has, the options are withdrawing or decreeing. */}
              {!active.extensionGrantedAt && !desistimientoDue && (left == null || left >= 0) && (
                <Button variant="outlined" disabled={busy} onClick={() => onExtend(active.id)}>
                  Conceder prórroga
                </Button>
              )}
            </>
          )}
          {active.status === "sent" && desistimientoDue && canDecree && (
            <Button
              variant="contained"
              color="error"
              startIcon={<GavelIcon />}
              disabled={busy}
              onClick={() => setDecreeOpen(true)}
            >
              Decretar desistimiento
            </Button>
          )}
        </Stack>
      )}

      <ReturnPqrsdDialog
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        busy={busy}
        title="Retirar el requerimiento"
        description="El requerimiento dejará de estar vigente y el término de respuesta se reanudará con los días hábiles que quedaban."
        reasonLabel="Motivo"
        confirmLabel="Retirar"
        errorText="No fue posible retirar el requerimiento."
        onConfirm={async (reason) => {
          await onWithdraw(active.id, reason);
          setWithdrawOpen(false);
        }}
      />

      <ReturnPqrsdDialog
        open={decreeOpen}
        onClose={() => setDecreeOpen(false)}
        busy={busy}
        minLength={20}
        title="Decretar el desistimiento tácito"
        description={pqrsdText("Se radicará un acto administrativo motivado, se archivará la PQRSD y se notificará al ciudadano que contra la decisión procede el recurso de reposición. Describa la motivación.")}
        signatureAction="decretado y firmado"
        reasonLabel="Motivación del acto"
        confirmLabel="Decretar y archivar"
        errorText="No fue posible decretar el desistimiento."
        onConfirm={async (reason) => {
          await onDecree(reason);
          setDecreeOpen(false);
        }}
      />

      <Dialog
        open={approveOpen}
        onClose={() => { setApproveOpen(false); setApproveSigned(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Aprobar y enviar el requerimiento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Se radicará el requerimiento, se enviará al ciudadano y el término de respuesta quedará
            suspendido. El documento se firma con su nombre, y abajo se acredita a quien lo redactó.
          </Typography>
          <RegisterSignature
            actionName="aprobado y firmado"
            edit={!busy}
            signed={approveSigned}
            setSigned={setApproveSigned}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setApproveOpen(false); setApproveSigned(false); }}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={busy || !approveSigned}
            onClick={async () => {
              await onApprove(active.id);
              setApproveOpen(false);
              setApproveSigned(false);
            }}
          >
            Aprobar y enviar
          </Button>
        </DialogActions>
      </Dialog>

      <ReturnPqrsdDialog
        open={rejectOpen}
        onClose={() => { setRejectOpen(false); setRejectComment(""); }}
        busy={busy}
        title="Devolver el requerimiento a borrador"
        description="Vuelve a manos de quien lo redactó, con su comentario. No se envía nada al ciudadano."
        reasonLabel="Qué hay que corregir"
        confirmLabel="Devolver"
        errorText="No fue posible devolver el requerimiento."
        onConfirm={async (comment) => {
          await onReject(active.id, comment || rejectComment);
          setRejectOpen(false);
          setRejectComment("");
        }}
      />
    </Paper>
  );
}
